import React from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Stack,
} from '@mui/material';
import {
  Inbox,
  Clock,
  CheckCircle,
  TrendingUp,
  Activity,
  AlertTriangle,
} from 'lucide-react';

export const StatCards = ({
  total = 0,
  ready = 0,
  unacked = 0,
  ackRate = 0,
  deliverRate = 0,
}) => {
  const cards = [
    {
      title: 'Total Messages',
      value: total,
      unit: 'messages',
      subtitle: 'Sum of Ready + Unacked',
      color: '#64748b',
      cardBg: '#f8fafc',
      cardBorder: '#cbd5e1',
      titleColor: '#475569',
      subtitleColor: '#64748b',
      bgLight: 'rgba(100, 116, 139, 0.15)',
      icon: <Inbox style={{ width: 16, height: 16 }} />,
    },
    {
      title: 'Ready to Deliver',
      value: ready,
      unit: 'ready',
      subtitle: 'Waiting in queue',
      color: '#d97706',
      cardBg: '#fffbeb',
      cardBorder: '#fde68a',
      titleColor: '#92400e',
      subtitleColor: '#b45309',
      bgLight: 'rgba(217, 119, 6, 0.15)',
      icon: <Clock style={{ width: 16, height: 16 }} />,
    },
    {
      title: 'Unacknowledged',
      value: unacked,
      unit: 'in processing',
      subtitle: 'Delivered to worker',
      color: '#0284c7',
      cardBg: '#f0f9ff',
      cardBorder: '#bae6fd',
      titleColor: '#075985',
      subtitleColor: '#0284c7',
      bgLight: 'rgba(2, 132, 199, 0.15)',
      icon: <AlertTriangle style={{ width: 16, height: 16 }} />,
    },
    {
      title: 'Consumer Ack Rate',
      value: `${Number(ackRate || 0).toFixed(2)}/s`,
      unit: '',
      subtitle: 'Acks confirmed',
      color: '#059669',
      cardBg: '#f0fdf4',
      cardBorder: '#bbf7d0',
      titleColor: '#166534',
      subtitleColor: '#15803d',
      bgLight: 'rgba(5, 150, 105, 0.15)',
      icon: <CheckCircle style={{ width: 16, height: 16 }} />,
    },
    {
      title: 'Deliver Rate',
      value: `${Number(deliverRate || 0).toFixed(2)}/s`,
      unit: '',
      subtitle: 'Dispatched to consumers',
      color: '#7c3aed',
      cardBg: '#faf5ff',
      cardBorder: '#e9d5ff',
      titleColor: '#581c87',
      subtitleColor: '#7e22ce',
      bgLight: 'rgba(124, 58, 237, 0.15)',
      icon: <Activity style={{ width: 16, height: 16 }} />,
    },
  ];

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          sm: 'repeat(2, 1fr)',
          md: 'repeat(3, 1fr)',
          lg: 'repeat(5, 1fr)',
        },
        gap: 2,
        width: '100%',
      }}
    >
      {cards.map((card, idx) => (
        <Card
          key={idx}
          sx={{
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            position: 'relative',
            overflow: 'hidden',
            cursor: 'pointer',
            border: '1.5px solid',
            borderColor: card.cardBorder,
            bgcolor: card.cardBg,
            borderRadius: 2,
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.03)',
            transition: 'all 0.24s cubic-bezier(0.4, 0, 0.2, 1)',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '3px',
              bgcolor: card.color,
              opacity: 0.85,
              transition: 'opacity 0.24s ease, height 0.24s ease',
            },
            '&:hover': {
              transform: 'translateY(-4px)',
              borderColor: card.color,
              boxShadow: `0 12px 24px -4px ${card.color}33, 0 4px 10px -2px rgba(0, 0, 0, 0.05)`,
              '&::before': {
                opacity: 1,
                height: '4px',
              },
              '& .stat-icon-box': {
                transform: 'scale(1.15)',
              },
            },
          }}
        >
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5}>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: card.titleColor,
                  fontSize: '0.7rem',
                }}
              >
                {card.title}
              </Typography>
              <Box
                className="stat-icon-box"
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: 1.5,
                  bgcolor: card.bgLight,
                  color: card.color,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'transform 0.2s ease',
                }}
              >
                {card.icon}
              </Box>
            </Stack>

            <Stack direction="row" alignItems="baseline" spacing={1}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  color: card.color,
                  letterSpacing: '-0.02em',
                  fontFamily: 'monospace',
                }}
              >
                {card.value}
              </Typography>
              {card.unit && (
                <Typography variant="caption" sx={{ color: card.subtitleColor, fontWeight: 600 }}>
                  {card.unit}
                </Typography>
              )}
            </Stack>

            <Stack direction="row" alignItems="center" spacing={0.8} mt={1}>
              {card.title.includes('Ack') ? (
                <TrendingUp style={{ width: 12, height: 12, color: card.color }} />
              ) : (
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: card.color,
                  }}
                />
              )}
              <Typography variant="caption" sx={{ color: card.subtitleColor, fontSize: '0.7rem', fontWeight: 600 }}>
                {card.subtitle}
              </Typography>
            </Stack>
          </CardContent>
        </Card>
      ))}
    </Box>
  );
};
